/**
 * The `@` trigger — the old web's `DocumentContextSuggestion.tsx` on TipTap's
 * `Suggestion`. `items()` is a three-branch state machine:
 *
 *   1. empty query        → the type keys (localised) + the skill options;
 *   2. query is a key     → that type's items (dual-path match: technical key
 *                           or its localised name);
 *   3. anything else      → a search across every type, debounced 300 ms,
 *                           with the matching skill options first (undebounced).
 *
 * `command()` has two paths. A **key** inserts transient text `@Key` — the
 * technical key, not the label: with `allowSpaces: false` a label with a
 * space ("CAD 連結", "Wall Type") would cut the query at the space and the item
 * list would never open. An **item** replaces the range with a mention node
 * and a trailing space.
 *
 * The config comes through a ref so the closures never go stale, and 2.5 adds
 * `refresh`, called when the menu opens — the no-bridge substitute for the
 * old "document changed" push (see `useDocumentContext`).
 */
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import type { MutableRefObject } from "react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import { MentionList, type MentionListRef } from "../components/chat/MentionList";
import type { DocumentContextItem, DocumentContextSearchHit } from "../lib/documentContext";
import { toDocumentContextMentionAttrs } from "../lib/documentContextMention";
import {
  buildItemOptions,
  buildKeyOptions,
  buildSearchOption,
  filterSkillOptions,
  findMatchingKey,
  type MentionOption,
} from "../lib/mentionOptions";

export const SEARCH_DEBOUNCE_MS = 300;

export interface DocumentContextSuggestionConfig {
  revitVersion: string | null;
  canBrowseDocumentContext: boolean;
  loadingKeys: boolean;
  keys: string[];
  displayNames: Record<string, string>;
  typeItems: DocumentContextItem[];
  searchResults: DocumentContextSearchHit[];
  fetchKeys: () => Promise<{ keys: string[]; displayNames: Record<string, string> }>;
  fetchTypeItems: (type: string) => Promise<DocumentContextItem[]>;
  searchItems: (query: string) => Promise<DocumentContextSearchHit[]>;
  refresh?: () => Promise<void>;
  skillItems: MentionOption[];
}

export function createDocumentContextSuggestion(
  configRef: MutableRefObject<DocumentContextSuggestionConfig>,
): Omit<SuggestionOptions<MentionOption, MentionOption>, "editor"> {
  let lastQuery: string | null = null;
  let hasFetchedKeys = false;
  let searchTimeout: ReturnType<typeof setTimeout> | null = null;

  const cleanupTimeout = () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }
  };

  return {
    char: "@",
    allowSpaces: false,
    allowedPrefixes: [" ", "\n"],

    items: async ({ query }): Promise<MentionOption[]> => {
      const config = configRef.current;
      if (!config.revitVersion || !config.canBrowseDocumentContext) return [];
      const trimmed = query.trim();

      if (!trimmed) {
        if (!hasFetchedKeys && !config.loadingKeys && config.keys.length === 0) {
          hasFetchedKeys = true;
          const result = await config.fetchKeys();
          return [...buildKeyOptions(result.keys, result.displayNames), ...config.skillItems];
        }
        return [...buildKeyOptions(config.keys, config.displayNames), ...config.skillItems];
      }
      hasFetchedKeys = false;

      const matchingKey = findMatchingKey(trimmed, config.keys, config.displayNames);
      if (matchingKey) {
        if (lastQuery !== trimmed) {
          lastQuery = trimmed;
          const items = await config.fetchTypeItems(matchingKey);
          return buildItemOptions(items, matchingKey, configRef.current.displayNames);
        }
        return buildItemOptions(config.typeItems, matchingKey, config.displayNames);
      }

      const skillHits = filterSkillOptions(config.skillItems, trimmed);
      if (lastQuery !== trimmed) {
        lastQuery = trimmed;
        cleanupTimeout();
        return new Promise((resolve) => {
          searchTimeout = setTimeout(async () => {
            const latest = configRef.current;
            const hits = await latest.searchItems(trimmed);
            resolve([...skillHits, ...hits.map((hit) => buildSearchOption(hit, latest.displayNames))]);
          }, SEARCH_DEBOUNCE_MS);
        });
      }
      return [...skillHits, ...config.searchResults.map((hit) => buildSearchOption(hit, config.displayNames))];
    },

    render: () => {
      let component: ReactRenderer<MentionListRef> | null = null;
      let popup: TippyInstance[] | null = null;

      const rect = (props: SuggestionProps<MentionOption>) => () => props.clientRect?.() ?? new DOMRect();

      return {
        onStart: (props) => {
          void configRef.current.refresh?.();
          component = new ReactRenderer(MentionList, { props: { items: props.items, command: props.command }, editor: props.editor });
          if (!props.clientRect) return;
          popup = tippy("body", {
            getReferenceClientRect: rect(props),
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "top-start",
          });
        },
        onUpdate: (props) => {
          component?.updateProps({ items: props.items, command: props.command });
          if (!props.clientRect) return;
          popup?.[0]?.setProps({ getReferenceClientRect: rect(props) });
        },
        onKeyDown: (props) => {
          if (props.event.key === "Escape") {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },
        onExit: () => {
          popup?.[0]?.destroy();
          component?.destroy();
          popup = null;
          component = null;
          lastQuery = null;
          hasFetchedKeys = false;
          cleanupTimeout();
        },
      };
    },

    command: ({ editor, range, props }) => {
      const config = configRef.current;
      const item = props;
      if (!config.canBrowseDocumentContext) return;

      if (item.type === "key") {
        editor.chain().focus().deleteRange(range).insertContent(`@${item.value}`).run();
        void config.fetchTypeItems(item.value);
        return;
      }

      const attrs = toDocumentContextMentionAttrs({
        type: item.typeName ?? "",
        id: item.id ?? "",
        name: item.value,
        ...(item.familyName ? { familyName: item.familyName } : {}),
        ...(item.revitTypeName ? { typeName: item.revitTypeName } : {}),
        ...(item.derivedFromTypeId ? { derivedFromTypeId: item.derivedFromTypeId } : {}),
        ...(item.derivedFromTypeName ? { derivedFromTypeName: item.derivedFromTypeName } : {}),
      });
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent([
          { type: "mention", attrs },
          { type: "text", text: " " },
        ])
        .run();
    },
  };
}
